import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import type { Permission, Session } from '../auth/OidcProvider';
import { matchPath } from 'react-router-dom';

export interface FrontendModule {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: ComponentType<LucideProps>;
  requiredPermissions: Permission[];
  /**
   * At least one of these, in addition to every `requiredPermissions` entry. The API reference is
   * one module over five services, each behind its own permission: all-of would demand every one,
   * so a user granted a single service's reference would see nothing.
   */
  requiredAnyPermission?: readonly Permission[];
  enabled: boolean;
  accent: string;
  navigation: readonly ModuleNavigationItem[];
}

export interface ModuleNavigationItem {
  title: string;
  route: string;
  icon: ComponentType<LucideProps>;
  end?: boolean;
  requiredPermission?: Permission;
}

export function isModuleAvailable(module: FrontendModule, session: Session) {
  return module.enabled
    && module.requiredPermissions.every((permission) => session.permissions.includes(permission))
    && (!module.requiredAnyPermission || module.requiredAnyPermission.some((permission) => session.permissions.includes(permission)));
}

export function isModuleNavigationItemAvailable(item: ModuleNavigationItem, session: Session) {
  return !item.requiredPermission || session.permissions.includes(item.requiredPermission);
}

/**
 * Whether `pathname` is inside the module rooted at `route`.
 *
 * React Router's own matcher, segment-wise rather than a plain string prefix: `/dam` must not claim `/damage`, or a future module
 * whose route happens to start with another module's name would open the wrong group. The module
 * route itself counts as inside, so landing on `/dam` opens Mediendaten just as `/dam/upload` does.
 */
export function isModuleRouteActive(route: string, pathname: string) {
  return matchPath({ path: route, end: false, caseSensitive: true }, pathname) !== null;
}

/** The id of the module the given path belongs to, or null on the portal overview. */
export function activeModuleId(modules: readonly FrontendModule[], pathname: string) {
  return modules.find((module) => isModuleRouteActive(module.route, pathname))?.id ?? null;
}
